import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useToast } from './Toast'
import { getCookieValue } from 'sr/utils/helper'
import { ACCESS_TOKEN_KEY } from 'sr/constants/common'
import { logAdminEvent } from 'sr/utils/api/adminAudit'

// Custom event name for same-tab localStorage sync
export const READ_ONLY_CHANGE_EVENT = 'janijobs:readonlychange'

/**
 * Set isReadOnly in localStorage and dispatch a custom event so
 * ReadOnlyModeProvider can sync its React state even within the same tab.
 * Use this instead of localStorage.setItem('isReadOnly', ...) directly.
 */
export const setReadOnlyStorage = (value: boolean) => {
  localStorage.setItem('isReadOnly', value.toString())
  window.dispatchEvent(new CustomEvent(READ_ONLY_CHANGE_EVENT, { detail: { isReadOnly: value } }))
}

interface ReadOnlyContextType {
  isReadOnly: boolean
  setReadOnly: (value: boolean) => void
  requestWriteMode: (onSuccess: () => void) => void
  blockMutation: () => boolean
}

const ReadOnlyContext = createContext<ReadOnlyContextType | undefined>(undefined)

export const ReadOnlyModeProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  // Initialize from localStorage to persist across page reloads
  const [isReadOnly, setIsReadOnlyState] = useState<boolean>(() => {
    const stored = localStorage.getItem('isReadOnly')
    if (stored === null || stored === undefined) {
      localStorage.setItem('isReadOnly', 'true')
      return true
    }
    return stored === 'true'
  })
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false)
  const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(null)
  const toast = useToast()

  const canEnterUpdateMode = useCallback(() => {
    const adminRole = localStorage.getItem('adminRole')
    if (adminRole === 'readonly') {
      return false
    }
    if (adminRole === 'superadmin') {
      return true
    }
    const storedPermissions = localStorage.getItem('adminPermissions')
    if (!storedPermissions) {
      return false
    }
    try {
      const permissions = JSON.parse(storedPermissions)
      return Boolean(permissions?.canUpdate)
    } catch (error) {
      return false
    }
  }, [])

  const setReadOnly = useCallback((value: boolean) => {
    setIsReadOnlyState(value)
    // Use setReadOnlyStorage so localStorage and React state always stay in sync
    localStorage.setItem('isReadOnly', value.toString())
    
    if (value) {
      toast.info('Read-Only Mode', 'Write operations are now disabled')
    } else {
      toast.success('Update Mode', 'Write operations are now enabled')
    }
  }, [toast])

  // Ensure localStorage is always initialized
  useEffect(() => {
    const stored = localStorage.getItem('isReadOnly')
    if (stored === null || stored === undefined) {
      localStorage.setItem('isReadOnly', 'true')
    }
    if (!canEnterUpdateMode() && stored === 'false') {
      localStorage.setItem('isReadOnly', 'true')
      setIsReadOnlyState(true)
      toast.error('Access denied', 'You do not have permission to enable Update mode.')
    }
  }, [canEnterUpdateMode, toast])

  // Listen for same-tab localStorage changes dispatched via setReadOnlyStorage()
  // This fixes the desync when login sets localStorage without going through React state
  useEffect(() => {
    const handleReadOnlyChange = (e: Event) => {
      const value = (e as CustomEvent<{isReadOnly: boolean}>).detail.isReadOnly
      setIsReadOnlyState(value)
    }
    window.addEventListener(READ_ONLY_CHANGE_EVENT, handleReadOnlyChange)
    return () => window.removeEventListener(READ_ONLY_CHANGE_EVENT, handleReadOnlyChange)
  }, [])

  const requestWriteMode = useCallback((onSuccess: () => void) => {
    if (!isReadOnly) {
      // Already in write mode
      onSuccess()
      return
    }
    if (!canEnterUpdateMode()) {
      toast.error('Access denied', 'You do not have permission to enable Update mode.')
      logAdminEvent({
        action: 'WRITE_BLOCKED_READONLY',
        entityType: 'system',
        metadata: {
          reason: 'insufficient_permissions'
        }
      })
      return
    }

    // Store callback and show password modal
    setPendingCallback(() => onSuccess)
    setShowPasswordModal(true)
  }, [isReadOnly, canEnterUpdateMode, toast])

  const blockMutation = useCallback((): boolean => {
    if (isReadOnly) {
      toast.error(
        'Write Operations Disabled',
        'You are in Read-Only mode. Please switch to Update mode to make changes.'
      )
      return true // Block the mutation
    }
    return false // Allow the mutation
  }, [isReadOnly, toast])

  return (
    <ReadOnlyContext.Provider value={{ isReadOnly, setReadOnly, requestWriteMode, blockMutation }}>
      {children}
      
      {/* Password Re-authentication Modal */}
      {showPasswordModal && (
        <PasswordReauthModal
          onSuccess={() => {
            setReadOnly(false)
            setShowPasswordModal(false)
            if (pendingCallback) {
              pendingCallback()
              setPendingCallback(null)
            }
          }}
          onCancel={() => {
            setShowPasswordModal(false)
            setPendingCallback(null)
          }}
        />
      )}
    </ReadOnlyContext.Provider>
  )
}

export const useReadOnlyMode = (): ReadOnlyContextType => {
  const context = useContext(ReadOnlyContext)
  if (!context) {
    throw new Error('useReadOnlyMode must be used within ReadOnlyModeProvider')
  }
  return context
}

// Password Re-authentication Modal Component
interface PasswordReauthModalProps {
  onSuccess: () => void
  onCancel: () => void
}

const PasswordReauthModal: React.FC<PasswordReauthModalProps> = ({ onSuccess, onCancel }) => {
  const [password, setPassword] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const toast = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // Get token from cookie (same pattern as the rest of the app)
      const accessToken = getCookieValue(ACCESS_TOKEN_KEY)
      
      if (!accessToken) {
        setError('Not authenticated. Please login again.')
        setIsLoading(false)
        return
      }

      // Call API to verify password
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ password }),
      })

      if (response.ok) {
        toast.success('Authentication Successful', 'Switched to Update mode')
        onSuccess()
      } else {
        const data = await response.json()
        setError(data.message || 'Invalid password')
      }
    } catch (err) {
      setError('Failed to verify password. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          Switch to Update Mode
        </h2>
        
        <p className="text-gray-600 mb-6">
          Please re-enter your password to enable write operations.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your password"
              required
              autoFocus
            />
            {error && (
              <p className="text-red-500 text-xs italic mt-2">{error}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Enable Update Mode'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

